// ABOUTME: React context of the dealership the current screen works in, its picker, and Assign → Dealership.
// ABOUTME: Mirrors the choice into the request-dealership store read by the fetch wrapper and Apollo link.

import { useAuth } from '@src/hooks';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { AssignNodeDialog } from '../../node-assignment/components/AssignNodeDialog';
import { fetchSelectableDealerships } from '../api/dealershipApi';
import { DealershipPickerDialog } from '../components/DealershipPickerDialog';
import {
    getLastDealershipChoice,
    setLastDealershipChoice,
    setRequestDealership as storeRequestDealership,
} from '../requestDealershipStore';
import type { DealershipChoice, DealershipPickerOptions, SelectableDealerships } from '../types';
import { DealershipChoiceContext } from './dealershipChoiceHooks';

export { useDealershipChoice, useOptionalDealershipChoice } from './dealershipChoiceHooks';

const EMPTY: SelectableDealerships = { dealerships: [], allowNone: false };

type AssignState = {
    hierarchyId: string;
    nodeId: string;
    resolve: (assigned: boolean) => void;
};

type PickerState = {
    selectable: SelectableDealerships;
    initialChoice?: DealershipChoice;
    optionCounts?: ReadonlyMap<string | undefined, number>;
    /** the change reloads a catalogue on screen: the picker says so */
    reloadsCatalogue: boolean;
    resolve: (choice: DealershipChoice | undefined) => void;
};

export function DealershipChoiceProvider({ children }: { children: ReactNode }) {
    // The last choice is kept per user: another user logging into the same tab starts afresh.
    const { sessionData } = useAuth();
    const username = sessionData?.username;
    // The cart starts on the dealership last picked in this tab; no screen works in one yet.
    const [lastChoice, setLastChoice] = useState<DealershipChoice | undefined>(() =>
        getLastDealershipChoice(username),
    );
    const [cartDealership, setCartDealership] = useState<string | undefined>(() =>
        lastChoice?.kind === 'dealership' ? lastChoice.dealershipId : undefined,
    );
    const [requestDealership, setRequestDealershipState] = useState<string | undefined>();
    // How many catalogue screens are mounted: two catalogue widgets can share a page.
    const catalogsMountedRef = useRef(0);
    const [picker, setPicker] = useState<PickerState | null>(null);
    const [assign, setAssign] = useState<AssignState | null>(null);
    const selectableRef = useRef<Promise<SelectableDealerships> | null>(null);

    // The store is written synchronously so the very next request already carries the header.
    const setRequestDealership = useCallback((dealershipId: string | undefined) => {
        storeRequestDealership(dealershipId);
        setRequestDealershipState(dealershipId);
    }, []);

    const rememberChoice = useCallback(
        (dealershipId: string | undefined) => {
            setLastDealershipChoice(username, dealershipId);
            setLastChoice(dealershipId ? { kind: 'dealership', dealershipId } : { kind: 'none' });
            setCartDealership(dealershipId);
        },
        [username],
    );

    const chooseRequestDealership = useCallback(
        (dealershipId: string | undefined) => {
            rememberChoice(dealershipId);
            setRequestDealership(dealershipId);
        },
        [rememberChoice, setRequestDealership],
    );

    const chooseCartDealership = useCallback(
        (dealershipId: string | undefined) => {
            rememberChoice(dealershipId);
            // On the catalogue the cart and the catalogue work in the same dealership.
            if (catalogsMountedRef.current > 0) setRequestDealership(dealershipId);
        },
        [rememberChoice, setRequestDealership],
    );

    const setCatalogActive = useCallback(
        (active: boolean) => {
            catalogsMountedRef.current = Math.max(
                0,
                catalogsMountedRef.current + (active ? 1 : -1),
            );
            // The dealership stays while another catalogue is still open.
            if (!active && catalogsMountedRef.current === 0) setRequestDealership(undefined);
        },
        [setRequestDealership],
    );

    const releaseRequestDealership = useCallback(
        () => setRequestDealership(undefined),
        [setRequestDealership],
    );

    const loadSelectable = useCallback(() => {
        if (!selectableRef.current) {
            // A failure is not "no dealerships": it rejects, and the next call retries.
            selectableRef.current = fetchSelectableDealerships().catch(error => {
                selectableRef.current = null;
                throw error;
            });
        }
        return selectableRef.current;
    }, []);

    // One picker at a time: a catalogue asking while another one's picker is open (two catalogue
    // widgets on a page) gets the same answer instead of replacing that picker.
    const pendingChoiceRef = useRef<Promise<DealershipChoice | undefined> | null>(null);
    const chooseDealership = useCallback(
        (initialChoice?: DealershipChoice, options?: DealershipPickerOptions) => {
            if (!pendingChoiceRef.current) {
                const pending = loadSelectable()
                    .then(
                        selectable =>
                            new Promise<DealershipChoice | undefined>(resolve => {
                                setPicker({
                                    selectable,
                                    initialChoice,
                                    optionCounts: options?.optionCounts,
                                    // From the cart too: on the catalogue page it switches both.
                                    reloadsCatalogue:
                                        !!options?.isChange && catalogsMountedRef.current > 0,
                                    resolve,
                                });
                            }),
                    )
                    .finally(() => {
                        pendingChoiceRef.current = null;
                    });
                pendingChoiceRef.current = pending;
            }
            return pendingChoiceRef.current;
        },
        [loadSelectable],
    );

    const close = useCallback(
        (choice: DealershipChoice | undefined) => {
            picker?.resolve(choice);
            setPicker(null);
        },
        [picker],
    );

    const assignNodeDealership = useCallback(
        (hierarchyId: string, nodeId: string) =>
            new Promise<boolean>(resolve => {
                setAssign({ hierarchyId, nodeId, resolve });
            }),
        [],
    );

    const closeAssign = useCallback(
        (assigned: boolean) => {
            assign?.resolve(assigned);
            setAssign(null);
        },
        [assign],
    );

    const value = useMemo(
        () => ({
            requestDealership,
            cartDealership,
            lastChoice,
            setRequestDealership,
            chooseRequestDealership,
            chooseCartDealership,
            setCatalogActive,
            releaseRequestDealership,
            loadSelectable,
            chooseDealership,
            assignNodeDealership,
        }),
        [
            requestDealership,
            cartDealership,
            lastChoice,
            setRequestDealership,
            chooseRequestDealership,
            chooseCartDealership,
            setCatalogActive,
            releaseRequestDealership,
            loadSelectable,
            chooseDealership,
            assignNodeDealership,
        ],
    );

    return (
        <DealershipChoiceContext.Provider value={value}>
            {children}
            <DealershipPickerDialog
                open={!!picker}
                selectable={picker?.selectable ?? EMPTY}
                initialChoice={picker?.initialChoice}
                optionCounts={picker?.optionCounts}
                reloadsCatalogue={picker?.reloadsCatalogue ?? false}
                onPick={close}
                onCancel={() => close(undefined)}
            />
            {assign && (
                <AssignNodeDialog
                    open
                    hierarchyId={assign.hierarchyId}
                    nodeId={assign.nodeId}
                    target="dealership"
                    onClose={() => closeAssign(false)}
                    onAssigned={() => closeAssign(true)}
                />
            )}
        </DealershipChoiceContext.Provider>
    );
}
